import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuthStore } from "@/lib/auth-store";
import { authApi } from "@/lib/api";
import { PaginaLoader } from "@/components/shared/PaginaLoader";

/**
 * Blokkeert routes voor niet-ingelogde gebruikers.
 * Haalt daarnaast de 'me' op zodra we een access-token hebben —
 * zo hebben sidebar/header altijd de actuele gebruiker.
 */
export function ProtectedRoute() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const gebruiker = useAuthStore((s) => s.gebruiker);
  const setGebruiker = useAuthStore((s) => s.setGebruiker);
  const logoutLokaal = useAuthStore((s) => s.logoutLokaal);
  const location = useLocation();

  const { data, isError, error } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.me,
    enabled: Boolean(accessToken) && !gebruiker,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data) setGebruiker(data);
  }, [data, setGebruiker]);

  useEffect(() => {
    if (isError) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) {
        logoutLokaal();
      }
    }
  }, [isError, error, logoutLokaal]);

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Wachten tot de gebruiker geladen is vóórdat we doorlaten: op de
  // allereerste render na een refresh/deeplink staat de query nog niet op
  // "fetching" en zou een kind-wachter (AdminRoute) op een lege stand
  // beslissen, waardoor /admin/* naar het Dashboard bouncete. Alleen bij
  // een echte fout laten we door; 401/403 logt hierboven al lokaal uit.
  if (!gebruiker && !isError) {
    return <PaginaLoader />;
  }

  return <Outlet />;
}
